'use client';

import { useState, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from '@/lib/toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { FileDropZone, FileRow } from '@/components/ui/file-drop-zone';
import { uploadFileToUrl } from '@/lib/uploads';
import { getFileTypeDescriptor } from '@/lib/services/files';
import {
  createFilePreview,
  revokeFilePreview,
  isImageFile
} from '@/lib/utils/file-utils';
import {
  getPresignedUrlAction,
  uploadCompleteAction
} from '@/lib/services/general-files';
import { profileApi } from '@/lib/services/profile/client';
import { FILE_TYPE_METADATA } from '@/lib/services/general-files/types';

interface SuggestionBoxDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface UploadedFile {
  id: string;
  file: File;
  fileId?: number;
  status: 'pending' | 'uploading' | 'complete' | 'error';
  error?: string;
  /** Upload progress 0–100 while `status === 'uploading'`. */
  progress?: number;
  /** Object URL for an image attachment thumbnail; revoked on remove/reset. */
  previewUrl?: string;
}

const MAX_FILES = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_SUGGESTION_LENGTH = 2000;

const ACCEPTED_MIME_TYPES =
  FILE_TYPE_METADATA['suggestion-attachment'].acceptedMimeTypes;

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function SuggestionBoxDialog({
  open,
  onOpenChange
}: SuggestionBoxDialogProps) {
  const [suggestion, setSuggestion] = useState('');
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = useCallback(() => {
    setSuggestion('');
    setFiles((prev) => {
      prev.forEach((f) => f.previewUrl && revokeFilePreview(f.previewUrl));
      return [];
    });
    setIsSubmitting(false);
  }, []);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      resetForm();
      onOpenChange(false);
    }
  }, [isSubmitting, onOpenChange, resetForm]);

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `File exceeds ${MAX_FILE_SIZE / (1024 * 1024)}MB limit`;
    }
    if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
      return 'File type not supported';
    }
    return null;
  };

  const addFiles = useCallback(
    (newFiles: FileList | File[]) => {
      const fileArray = Array.from(newFiles);
      const remainingSlots = MAX_FILES - files.length;

      if (remainingSlots <= 0) {
        toast.error(`Maximum ${MAX_FILES} files allowed`);
        return;
      }

      const filesToAdd = fileArray.slice(0, remainingSlots);
      const skippedCount = fileArray.length - filesToAdd.length;

      const validFiles: UploadedFile[] = [];
      const errors: string[] = [];

      filesToAdd.forEach((file) => {
        const error = validateFile(file);
        if (error) {
          errors.push(`${file.name}: ${error}`);
        } else {
          validFiles.push({
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            file,
            status: 'pending',
            previewUrl: isImageFile(file)
              ? (createFilePreview(file) ?? undefined)
              : undefined
          });
        }
      });

      if (errors.length > 0) {
        errors.forEach((error) => toast.error(error));
      }

      if (skippedCount > 0) {
        toast.warning(
          `Only ${filesToAdd.length} files added. Maximum ${MAX_FILES} files allowed.`
        );
      }

      setFiles((prev) => [...prev, ...validFiles]);
    },
    [files.length]
  );

  const removeFile = useCallback((fileId: string) => {
    setFiles((prev) => {
      const target = prev.find((f) => f.id === fileId);
      if (target?.previewUrl) revokeFilePreview(target.previewUrl);
      return prev.filter((f) => f.id !== fileId);
    });
  }, []);

  const uploadFile = async (
    uploadedFile: UploadedFile
  ): Promise<number | null> => {
    // Update status to uploading
    setFiles((prev) =>
      prev.map((f) =>
        f.id === uploadedFile.id
          ? { ...f, status: 'uploading', progress: 0 }
          : f
      )
    );

    try {
      // Get presigned URL
      const presignedResult = await getPresignedUrlAction({
        fileType: 'suggestion-attachment'
      });

      if (!presignedResult.success || !presignedResult.data) {
        throw new Error(presignedResult.error || 'Failed to get upload URL');
      }

      const { presignedUrl, fileId } = presignedResult.data;

      // Upload to S3 via the shared progress-capable primitive.
      await uploadFileToUrl(presignedUrl, uploadedFile.file, {
        contentType: uploadedFile.file.type || 'application/octet-stream',
        onProgress: (p) =>
          setFiles((prev) =>
            prev.map((f) =>
              f.id === uploadedFile.id ? { ...f, progress: p.percent } : f
            )
          )
      });

      // Complete upload
      const completeResult = await uploadCompleteAction({
        fileId,
        originalFilename: uploadedFile.file.name
      });

      if (!completeResult.success) {
        throw new Error(completeResult.error || 'Failed to complete upload');
      }

      // Update status to complete
      setFiles((prev) =>
        prev.map((f) =>
          f.id === uploadedFile.id ? { ...f, status: 'complete', fileId } : f
        )
      );

      return fileId;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Upload failed';

      // Update status to error
      setFiles((prev) =>
        prev.map((f) =>
          f.id === uploadedFile.id
            ? { ...f, status: 'error', error: errorMessage }
            : f
        )
      );

      return null;
    }
  };

  const handleSubmit = async () => {
    if (!suggestion.trim()) {
      toast.error('Please enter a suggestion');
      return;
    }

    if (suggestion.length > MAX_SUGGESTION_LENGTH) {
      toast.error(
        `Suggestion must be ${MAX_SUGGESTION_LENGTH} characters or less`
      );
      return;
    }

    setIsSubmitting(true);

    try {
      // Upload all pending files
      const fileIds: number[] = [];
      const uploadErrors: string[] = [];

      for (const file of files) {
        if (file.status === 'complete' && file.fileId) {
          fileIds.push(file.fileId);
        } else if (file.status === 'pending') {
          const fileId = await uploadFile(file);
          if (fileId) {
            fileIds.push(fileId);
          } else {
            uploadErrors.push(file.file.name);
          }
        }
      }

      // Show warning for failed uploads but continue
      if (uploadErrors.length > 0) {
        toast.warning(
          `Some files failed to upload: ${uploadErrors.join(', ')}`
        );
      }

      // Submit suggestion
      await profileApi.submitSuggestion({
        suggestion: suggestion.trim(),
        fileIds
      });

      toast.success(
        'Thank you for your suggestion! The Vale team has received it.'
      );
      resetForm();
      onOpenChange(false);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to submit suggestion';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = suggestion.trim().length > 0 && !isSubmitting;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">
            Suggestion Box
          </DialogTitle>
          <DialogDescription className="text-grey-neutral text-xs font-medium">
            Share your feedback below to help make Vale better.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Textarea */}
          <Textarea
            value={suggestion}
            onChange={(e) => setSuggestion(e.target.value)}
            className="bg-input-bg min-h-[200px] resize-none"
            disabled={isSubmitting}
            maxLength={MAX_SUGGESTION_LENGTH}
          />

          {/* Drop zone — shared FileDropZone (mini), selection-only: files
              upload on submit, so no onUpload handler here. */}
          <FileDropZone
            variant="mini"
            multiple
            accept={ACCEPTED_MIME_TYPES.join(',')}
            title="Click or drag to upload"
            hintText="PDF, JPG, PNG · up to 5 MB"
            disabled={isSubmitting}
            onFilesAdded={addFiles}
          />

          {/* File list — shared FileRow (image attachments show a thumbnail) */}
          {files.length > 0 && (
            <div className="space-y-2">
              {files.map((file) => (
                <FileRow
                  key={file.id}
                  label={
                    getFileTypeDescriptor(file.file.type, file.file.name).label
                  }
                  previewUrl={file.previewUrl}
                  name={file.file.name}
                  status={
                    file.status === 'complete'
                      ? 'success'
                      : file.status === 'uploading'
                        ? 'uploading'
                        : file.status === 'error'
                          ? 'error'
                          : 'idle'
                  }
                  progress={
                    file.status === 'uploading' ? file.progress : undefined
                  }
                  sizeLabel={formatFileSize(file.file.size)}
                  error={file.error}
                  onRemove={
                    isSubmitting ? undefined : () => removeFile(file.id)
                  }
                />
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
